import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Box,
  Card,
  CircularProgress,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { styled, keyframes } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { useFileUploadContext } from "../../../context/FileUploadContext";
import { lockEnabledFileTypes } from "../../../context/FileUploadContext";
import Grid from "@mui/material/Grid";
import useStyles from "./FileUpload.styles";
import { useUserContext } from "../../../context/UserContext";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const slowBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
`;

const FileUpload = () => {
  const { classes } = useStyles();
  const { loggedInUserEmail } = useUserContext() || {};

  const {
    rows,
    attachFiles,
    removeFiles,
    uploadFiles,
    uploadAndReplaceFiles,
    downloadFile,
    checkInFile,
    checkOutFile,
  } = useFileUploadContext();

  const onFileChange = (index, e) => {
    try {
      const files = Array.from(e.target.files || []);
      if (!files?.length) return;
      attachFiles(index, files);
      e.target.value = ""; // Reattaching same file can be done
    } catch (err) {
      alert(err.message);
      e.target.value = "";
    }
  };

  const isLockEnabledType = (type) => lockEnabledFileTypes.includes(type);

  /*
  // Can be used even before locked in (if required)
  const canUseRow = (row) => {
    if (!isLockEnabledType(row.type)) return true;
    if (!row.checkInStatus) return true;
    return row.checkInBy === loggedInUserEmail;
  };*/

  const isCheckedInByMe = (row) => {
    if (!isLockEnabledType(row.type)) return true;
    return row.checkInStatus && row.checkInBy === loggedInUserEmail;
  };

  return (
    <div className={classes.page}>
      <Card
        style={{
          padding: "6px",
          marginBottom: "20px",
          width: "100%",
        }}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 12 }}>
            {!rows || !rows.length ? (
              <Item>
                <Typography
                  variant="p"
                  component="p"
                  sx={{
                    color: "#adadad",
                    minHeight: "250px",
                  }}
                >
                  Sorry, You dont have access / write permission to Upload
                  files!
                </Typography>
              </Item>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Number</TableCell>
                    <TableCell align="center">Title</TableCell>
                    <TableCell align="center">Attach File</TableCell>
                    <TableCell align="center">Upload</TableCell>
                    <TableCell align="center">Remove</TableCell>
                    <TableCell align="center">Download</TableCell>
                    <TableCell align="center" sx={{ width: 150, p: 1 }}>
                      Check In / Check Out
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows?.map((row, index) => (
                    <TableRow
                      key={row.type}
                      sx={{
                        opacity: row.uploading ? 0.6 : 1,
                        pointerEvents: row.uploading ? "none" : "auto",
                      }}
                    >
                      <TableCell align="center">{index + 1}</TableCell>

                      <TableCell align="center">{row.title}</TableCell>

                      <TableCell align="center">
                        <input
                          type="file"
                          multiple={!!row.isMultiFileSupport}
                          accept={row.accept}
                          hidden
                          id={`file-input-${row.type}`}
                          disabled={row.uploading || !isCheckedInByMe(row)}
                          onChange={(e) => onFileChange(index, e)}
                        />
                        <label htmlFor={`file-input-${row.type}`}>
                          <IconButton
                            component="span"
                            disabled={row.uploading || !isCheckedInByMe(row)}
                          >
                            <AttachFileIcon />
                          </IconButton>
                          {row.files && row.files.length > 0
                            ? row.files?.map((file) => file.name).join(", ")
                            : row.accept
                              ? `(Accepts: ${row.accept})`
                              : ""}
                        </label>
                      </TableCell>

                      <TableCell align="center" sx={{ position: "relative" }}>
                        {row.canUploadAndUpdate && (
                          <Button
                            variant="contained"
                            onClick={() => uploadFiles(index)}
                            disabled={
                              !row.files ||
                              row.files?.length === 0 ||
                              row.uploading ||
                              !isCheckedInByMe(row)
                            }
                          >
                            Upload & Update
                          </Button>
                        )}

                        {row.canUploadAndReplace && (
                          <Button
                            variant="contained"
                            sx={{
                              bgcolor: "red",
                              left: row.canUploadAndUpdate && "20px",
                            }}
                            onClick={() => uploadAndReplaceFiles(index)}
                            disabled={
                              !row.files ||
                              row.files?.length === 0 ||
                              row.uploading ||
                              !isCheckedInByMe(row)
                            }
                          >
                            Upload & Replace
                          </Button>
                        )}
                        {/* Row-level overlay */}
                        {row.uploading && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              bgcolor: "rgba(0,0,0,0.4)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 2,
                            }}
                          >
                            <CircularProgress
                              size={32}
                              sx={{ color: "white" }}
                            />
                          </Box>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {row.files && (
                          <IconButton
                            onClick={() => removeFiles(index)}
                            disabled={row.uploading || !isCheckedInByMe(row)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {row.isDownloadable && (
                          <IconButton
                            onClick={() =>
                              downloadFile(row.type, row.downloadFormat)
                            }
                            disabled={!isCheckedInByMe(row)}
                          >
                            <DownloadIcon />
                          </IconButton>
                        )}
                      </TableCell>

                      <TableCell align="center" sx={{ width: 150, p: 1 }}>
                        {isLockEnabledType(row.type) && (
                          <Box
                            sx={{
                              display: "inline-flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "fit-content",
                              mx: "auto",
                            }}
                          >
                            {!row.checkInStatus && (
                              <>
                                <IconButton
                                  onClick={() => checkInFile(index)}
                                  disabled={row.uploading}
                                  sx={{ p: 0.25 }}
                                >
                                  <LockOpenIcon />
                                </IconButton>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.75rem",
                                    lineHeight: 1,
                                    textAlign: "center",
                                    mt: 0.25,
                                    animation: `${slowBlink} 2s ease-in-out infinite`,
                                  }}
                                >
                                  Lock in here to update
                                </Typography>
                              </>
                            )}

                            {row.checkInStatus &&
                              row.checkInBy === loggedInUserEmail && (
                                <>
                                  <IconButton
                                    onClick={() => checkOutFile(index)}
                                    disabled={row.uploading}
                                    sx={{ p: 0.25 }}
                                  >
                                    <LockIcon />
                                  </IconButton>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.75rem",
                                      lineHeight: 1,
                                      textAlign: "center",
                                      mt: 0.25,
                                      color: "green",
                                      animation: `${slowBlink} 2s ease-in-out infinite`,
                                    }}
                                  >
                                    Locked in by you <br />
                                    (Unlock once done)
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.65rem",
                                      lineHeight: 2,
                                      textAlign: "center",
                                      whiteSpace: "nowrap",
                                      color: "gray",
                                    }}
                                  >
                                    {row.checkInTime
                                      ? new Date(
                                          row.checkInTime,
                                        ).toLocaleString()
                                      : ""}
                                  </Typography>
                                </>
                              )}

                            {row.checkInStatus &&
                              row.checkInBy !== loggedInUserEmail && (
                                <>
                                  <IconButton disabled sx={{ p: 0.25 }}>
                                    <LockIcon />
                                  </IconButton>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.75rem",
                                      lineHeight: 1,
                                      textAlign: "center",
                                      mt: 0.25,
                                      color: "red",
                                    }}
                                  >
                                    Locked in by <br /> {row.checkInBy} <br />
                                    (Unlock once done)
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.65rem",
                                      lineHeight: 2,
                                      textAlign: "center",
                                      whiteSpace: "nowrap",
                                      color: "gray",
                                    }}
                                  >
                                    {row.checkInTime
                                      ? new Date(
                                          row.checkInTime,
                                        ).toLocaleString()
                                      : ""}
                                  </Typography>
                                </>
                              )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Grid>
        </Grid>
      </Card>
    </div>
  );
};

export default FileUpload;
